import type {ReactElement} from 'react';
import {useEffect, useMemo, useRef, useState} from 'react';
import {Text, TextSize} from '@Components/text';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Button, ButtonSize, ButtonType} from '@Components/button';
import {ClickableDiv} from '@Components/clickable-div';
import {SkeletonBrick} from '@Components/skeleton-brick/skeleton-brick';
import type {Brand, BrandTemplate} from '@Components/mystuff-brands/brand-kits.types';
import {BrandAssetName, BrandAssetType, BrandsLoadingState} from '@Components/mystuff-brands/brand-kits.types';
import {fetchBrandAssets} from '@Components/mystuff-brands/brands-thunk';
import {BrandKitPill} from '@Components/mystuff-brands/components/brand-kit-pill-row/components/brand-kit-pill/brand-kit-pill';
import {getClusterForKit, getHeadingFontFamilyForKit} from '@Components/mystuff-brands/components/brand-kit-pill-row/brand-kit-pill-row.helper';
import {BrandKitSetupPrompt} from '@Components/mystuff-brands/components/brand-kit-setup-prompt/brand-kit-setup-prompt';
import {openBrandKitsSetup} from '@Libraries/brand-kit-creation.library';
import {getBrandsWithoutTemporaryIds} from '@Libraries/brands-library';
import {useActiveBrandId} from '@Hooks/brands/useActiveBrandId';
import {useBrandColorsMap} from '@Hooks/brands/useBrandColorsMap';
import {useBrandContentMap} from '@Hooks/brands/useBrandContentMap';
import {useBrandFontsMap} from '@Hooks/brands/useBrandFontsMap';
import {useBrandLogosMap} from '@Hooks/brands/useBrandLogosMap';
import {useBrandsLoadingState} from '@Hooks/brands/useBrandsLoadingState';
import {useLoadBrandKitAssets} from '@Hooks/brands/useLoadBrandKitAssets';
import {useSortedBrandKitsFromStore} from '@Hooks/brands/useSortedBrandKitsFromStore';
import {getAssetUrl} from '@Utils/s3.util';
import {noop} from '@Utils/general.util';
import {useAppSelector} from '@/hooks';
import type {BrandTemplatesTabProps} from './brand-templates-tab.types';
import {
  BRAND_TEMPLATE_PAGE_HASHED_ID,
  BRAND_TEMPLATES_EMPTY_ILLUSTRATION_PATH,
  BRAND_TEMPLATES_SKELETON_CARD_COUNT,
  BRAND_TEMPLATES_SKELETON_NAME_HEIGHT,
  BRAND_TEMPLATES_SKELETON_PILL_HEIGHT,
  BRAND_TEMPLATES_SKELETON_PILL_WIDTHS,
  BRAND_TEMPLATES_SKELETON_THUMBNAIL_HEIGHT,
} from './brand-templates-tab.types';
import styles from './brand-templates-tab.module.scss';

/**
 * The designs modal's Brand templates tab: the user's kits as pills and the chosen kit's templates as a selectable grid.
 * Picking a pill only changes what this tab shows (the active kit stays); picks are handed back as design selections.
 */
export function BrandTemplatesTab({selectedDesigns, onSelectionChange, onAddBrandTemplates, excludedBrandId, isShown}: BrandTemplatesTabProps): ReactElement {
  useLoadBrandKitAssets(true);
  const brandsLoadingState = useBrandsLoadingState();
  const activeBrandId = useActiveBrandId();
  const sortedBrandKits = useSortedBrandKitsFromStore();
  const brandKits = useMemo(() => {
    return getBrandsWithoutTemporaryIds(sortedBrandKits).filter((brandKit) => {
      return brandKit.brandId !== excludedBrandId;
    });
  }, [sortedBrandKits, excludedBrandId]);
  const [shownBrandId, setShownBrandId] = useState<string | null>(null);
  const kitPillsRef = useRef<HTMLDivElement>(null);
  const brandContent = useBrandContentMap();
  const templatesById = useAppSelector(({brands}) => {
    return brands.templates;
  });
  const colors = useBrandColorsMap();
  const logos = useBrandLogosMap();
  const fonts = useBrandFontsMap();
  const selectedPosterHashedIds = useMemo(() => {
    return new Set(
      selectedDesigns.map((design) => {
        return design.posterHashedId;
      })
    );
  }, [selectedDesigns]);

  const shownBrandKit = getShownBrandKit(brandKits, shownBrandId ?? activeBrandId ?? null);
  const shownTemplatesSlot = shownBrandKit === null ? undefined : brandContent[shownBrandKit.brandId]?.[BrandAssetName.TEMPLATES];

  useEffect(() => {
    if (shownBrandKit === null || shownTemplatesSlot?.loaded === BrandsLoadingState.LOADED || shownTemplatesSlot?.loaded === BrandsLoadingState.LOADING) {
      return;
    }

    fetchBrandAssets(shownBrandKit.brandId, BrandAssetType.BRAND_TEMPLATES).catch(noop);
  }, [shownBrandKit?.brandId, shownTemplatesSlot?.loaded]);

  /**
   * The row scrolls sideways, so the shown kit's pill is brought into it; it may sit far along a long row.
   * Hiding the tab drops the row's scroll, so it is done again each time the tab is shown.
   */
  useEffect(() => {
    if (!isShown) {
      return;
    }

    kitPillsRef.current?.querySelector('[aria-pressed="true"]')?.scrollIntoView({block: 'nearest', inline: 'nearest'});
  }, [shownBrandKit?.brandId, brandsLoadingState, isShown]);

  const shownTemplates = getPickableTemplates(shownTemplatesSlot?.ids ?? [], templatesById);

  const isTemplateSelected = (posterHashedId: string): boolean => {
    return selectedPosterHashedIds.has(posterHashedId);
  };

  const toggleTemplate = (posterHashedId: string): void => {
    if (isTemplateSelected(posterHashedId)) {
      onSelectionChange(
        selectedDesigns.filter((design) => {
          return design.posterHashedId !== posterHashedId;
        })
      );
      return;
    }

    onSelectionChange([...selectedDesigns, {posterHashedId, pageHashedId: BRAND_TEMPLATE_PAGE_HASHED_ID}]);
  };

  const getKitPill = (brandKit: Brand): ReactElement => {
    return (
      <BrandKitPill
        key={brandKit.brandId}
        brandKit={brandKit}
        isActive={brandKit.brandId === shownBrandKit?.brandId}
        cluster={getClusterForKit(brandKit.brandId, brandContent, {colors, logos})}
        headingFontFamily={getHeadingFontFamilyForKit(brandKit.brandId, brandContent, fonts)}
        onSelect={() => {
          setShownBrandId(brandKit.brandId);
        }}
      />
    );
  };

  /** A lone kit with nothing in it needs no pill to say which kit is empty; the handoff drops it. */
  const getKitPills = (): ReactElement | null => {
    if (brandKits.length === 1 && shownTemplates.length === 0) {
      return null;
    }

    return (
      <div ref={kitPillsRef} className={styles.kitPills}>
        {brandKits.map(getKitPill)}
      </div>
    );
  };

  const getSelectionCheckbox = (posterHashedId: string): ReactElement | null => {
    if (!isTemplateSelected(posterHashedId)) {
      return null;
    }

    return (
      <span className={`${styles.selectionCheckbox} flex-center bg-brand-primary-default corner-radius-8 ct-neutral-primary-on-dark _position-absolute`}>
        <Icon icon="icon-check" size={IconSize.SIZE_ICON_16} type={IconType.NONE} shape={IconShape.NONE} />
      </span>
    );
  };

  const getTemplateCard = (template: PickableBrandTemplate): ReactElement => {
    const isSelected = isTemplateSelected(template.posterHashedId);

    return (
      <li key={template.idbrandTemplate}>
        <ClickableDiv
          className={`${styles.templateCard} ${isSelected ? styles.selectedCard : ''} flex-v-row gap-2 _cursor-pointer`}
          aria-pressed={isSelected}
          aria-label={template.name ?? ''}
          onClick={() => {
            toggleTemplate(template.posterHashedId);
          }}
        >
          <div className={`${styles.templateThumbnail} flex-center corner-radius-12 spacing-p-2 _position-relative`}>
            <img className={`${styles.templateImage} corner-radius-4 _full-height _full-width`} src={template.previewUrl ?? ''} alt="" />
            {getSelectionCheckbox(template.posterHashedId)}
          </div>
          <Text val={template.name ?? ''} size={TextSize.MD} className={`${styles.templateName} -truncated-text`} />
        </ClickableDiv>
      </li>
    );
  };

  const getEmptyState = (): ReactElement => {
    return (
      <div className="flex-column-align-center gap-6 spacing-p-t-10 spacing-p-b-10">
        <img className={`${styles.emptyIllustration} _d-block`} src={getAssetUrl(BRAND_TEMPLATES_EMPTY_ILLUSTRATION_PATH)} alt="" aria-hidden="true" />
        <div className="flex-column-align-center gap-2 -text-align-center">
          <Text val={window.i18next.t('pmwjs_brand_templates_tab_empty_title')} className={`${styles.emptyTitle} ct-neutral-primary`} />
          <Text val={window.i18next.t('pmwjs_brand_templates_tab_empty_hint')} size={TextSize.MD} className={`${styles.emptyHint} ct-neutral-secondary`} />
        </div>
        <Button type={ButtonType.SECONDARY} size={ButtonSize.MEDIUM} icon="icon-plus" text={window.i18next.t('pmwjs_add_brand_templates')} onClick={onAddBrandTemplates} />
      </div>
    );
  };

  /** The grid's shape while the shown kit's templates load: grey cards where the thumbnails and names will be. */
  const getSkeletonGrid = (): ReactElement => {
    return (
      <ul className={styles.templatesGrid} aria-busy="true" aria-label={window.i18next.t('pmwjs_loading')}>
        {Array.from({length: BRAND_TEMPLATES_SKELETON_CARD_COUNT}, (_, index) => {
          return (
            <li key={`template-skeleton-${String(index)}`} className={`${styles.skeletonCard} flex-v-row gap-2`}>
              <SkeletonBrick width="100%" height={BRAND_TEMPLATES_SKELETON_THUMBNAIL_HEIGHT} radius="12px" />
              <SkeletonBrick width="70%" height={BRAND_TEMPLATES_SKELETON_NAME_HEIGHT} radius="4px" />
            </li>
          );
        })}
      </ul>
    );
  };

  /** Before the kits are in there are no pills to name either, so the row is drawn as grey pills too. */
  const getSkeleton = (): ReactElement => {
    return (
      <>
        <div className={styles.kitPills}>
          {BRAND_TEMPLATES_SKELETON_PILL_WIDTHS.map((width) => {
            return <SkeletonBrick key={`pill-skeleton-${String(width)}`} width={width} height={BRAND_TEMPLATES_SKELETON_PILL_HEIGHT} radius="999px" />;
          })}
        </div>
        {getSkeletonGrid()}
      </>
    );
  };

  const areAllShownTemplatesSelected = (): boolean => {
    return (
      shownTemplates.length > 0 &&
      shownTemplates.every((template) => {
        return isTemplateSelected(template.posterHashedId);
      })
    );
  };

  /** Selects every template of the shown kit, or clears them all when they already are; other kits' picks stay. */
  const toggleAllShownTemplates = (): void => {
    const shownPosterHashedIds = new Set(
      shownTemplates.map((template) => {
        return template.posterHashedId;
      })
    );

    if (areAllShownTemplatesSelected()) {
      onSelectionChange(
        selectedDesigns.filter((design) => {
          return !shownPosterHashedIds.has(design.posterHashedId);
        })
      );
      return;
    }

    const unselectedTemplates = shownTemplates.filter((template) => {
      return !isTemplateSelected(template.posterHashedId);
    });

    onSelectionChange([
      ...selectedDesigns,
      ...unselectedTemplates.map((template) => {
        return {posterHashedId: template.posterHashedId, pageHashedId: BRAND_TEMPLATE_PAGE_HASHED_ID};
      }),
    ]);
  };

  const getSelectAllAction = (): ReactElement => {
    return (
      <div className="flex-justify-end">
        <ClickableDiv className={`${styles.selectAllAction} ct-brand-primary _cursor-pointer`} onClick={toggleAllShownTemplates}>
          <Text val={window.i18next.t(areAllShownTemplatesSelected() ? 'pmwjs_deselect_all' : 'pmwjs_select_all')} size={TextSize.MD} bold />
        </ClickableDiv>
      </div>
    );
  };

  const getTemplatesContent = (): ReactElement => {
    if (shownTemplatesSlot?.loaded !== BrandsLoadingState.LOADED) {
      return getSkeletonGrid();
    }

    if (shownTemplates.length === 0) {
      return getEmptyState();
    }

    return (
      <>
        {getSelectAllAction()}
        <ul className={styles.templatesGrid}>{shownTemplates.map(getTemplateCard)}</ul>
      </>
    );
  };

  const getContent = (): ReactElement => {
    if (brandsLoadingState !== BrandsLoadingState.LOADED) {
      return getSkeleton();
    }

    if (shownBrandKit === null) {
      return <BrandKitSetupPrompt onSetUp={openBrandKitsSetup} />;
    }

    return (
      <>
        {getKitPills()}
        {getTemplatesContent()}
      </>
    );
  };

  return <div className={`${styles.brandTemplatesTab} flex-v-row gap-5 spacing-m-x-auto spacing-p-t-8 spacing-p-b-8 _full-width`}>{getContent()}</div>;
}

/** A template whose design still exists, which is the only kind the modal can hand back. */
interface PickableBrandTemplate extends BrandTemplate {
  posterHashedId: string;
}

const isPickableTemplate = (template: BrandTemplate | undefined): template is PickableBrandTemplate => {
  return template !== undefined && template.posterHashedId !== null;
};

const getPickableTemplates = (templateIds: number[], templatesById: Record<string, BrandTemplate>): PickableBrandTemplate[] => {
  return templateIds
    .map((templateId) => {
      return templatesById[templateId];
    })
    .filter(isPickableTemplate);
};

/** The kit the user picked in the tab, else their active kit, else the first one they have. */
const getShownBrandKit = (brandKits: Brand[], preferredBrandId: string | null): Brand | null => {
  const preferredKit = brandKits.find((brandKit) => {
    return brandKit.brandId === preferredBrandId;
  });

  return preferredKit ?? brandKits[0] ?? null;
};
